import { useState } from 'react';
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '@/components/ui/collapsible';
import { Sparkles, ChevronDown, ChevronUp } from 'lucide-react';

/**
 * Shared "What's New in Precision Fish Detection" block that appears at
 * the top of every *Benefits.js page. Single source of truth for platform-
 * wide announcements so every feature page stays in sync.
 *
 * Props:
 *   - highlight (string, optional): id of one item to visually emphasise
 *     (e.g. 'multi-species' on SFRCalculatorBenefits so that page calls out
 *     the species activation first). Not required.
 */
export default function WhatsNewSection({ highlight = null }) {
  const [open, setOpen] = useState(true);

  const items = [
    {
      id: 'multi-species',
      badge: 'Feb 2026',
      title: 'Seabream, Seabass and Tilapia — now available to everyone',
      body: (
        <>
          All three production species are unlocked for every account. Seabass
          uses the full SGR+temperature model (same pattern as Seabream) and
          Tilapia uses a static time-driven curve. Species-specific BFCR and
          SFR tables stay strictly siloed so feed, growth and missing-fish
          maths never cross-contaminate between species.
        </>
      ),
    },
    {
      id: 'master-growth-adjustment',
      badge: 'Feb 2026',
      title: 'Master growth adjustment flows across every species, everywhere',
      body: (
        <>
          The model adjustment % (50–150 %) now scales the growth curve
          consistently across Growth vs Model, Growth Prognosis and
          per-sample variance — for Seabream, Seabass, Tilapia and Tripletail.
          Verified monotonic: +20 % → proportionally fewer days to target,
          −20 % → more days.
        </>
      ),
    },
    {
      id: 'guard-rails-toggle',
      badge: 'Feb 2026',
      title: 'Guard Rails OFF / Indicative Report Mode — cross-species parity',
      body: (
        <>
          The Indicative Report toggle now surfaces the amber adjusted line
          and Missing Fish Intelligence consistently for Seabream, Seabass and
          Tilapia, even when a record's growth direction is below model but
          FCR-based analysis still indicates a stock discrepancy. Toggling
          Guard Rails OFF also bypasses the Tilapia safeguard clamp so all
          species behave identically under override.
        </>
      ),
    },
    {
      id: 'missing-fish-ssot',
      badge: 'Feb 2026',
      title: 'Missing Fish Intelligence: single source of truth line',
      body: (
        <>
          The amber "Adjusted Actual" line on Spreadsheet reports now passes
          exactly through your sample weights using piecewise interpolation,
          representing what each fish's weight would average if the population
          matched the FCR-implied adjusted count. One line, one story — no
          more contradictions between the FCR panel and the growth chart.
        </>
      ),
    },
    {
      id: 'seabass-prognosis-region',
      badge: 'Feb 2026',
      title: 'Seabass Growth Prognosis — temperature region wired in',
      body: (
        <>
          Seabass now exposes the full Temperature Region dropdown on the
          Growth Prognosis form (same as Seabream) and validates inline
          before submission. Unknown or deleted regions return a clean,
          human-readable error instead of a crash.
        </>
      ),
    },
    {
      id: 'tilapia-static-banner',
      badge: 'Feb 2026',
      title: 'Softer "static model" banner for Tilapia',
      body: (
        <>
          The banner that explains Tilapia's static temperature-agnostic
          curve has been redesigned — same information, half the visual
          weight, so it no longer dominates the form.
        </>
      ),
    },
  ];

  return (
    <Collapsible open={open} onOpenChange={setOpen}>
      <CollapsibleTrigger asChild>
        <button
          className="w-full flex items-center justify-between p-3 sm:p-4 bg-gradient-to-r from-amber-50 via-white to-amber-50 border border-amber-200 rounded-lg hover:from-amber-100 transition-colors"
          data-testid="whats-new-toggle"
        >
          <div className="flex items-center gap-2 sm:gap-3">
            <div className="p-2 bg-amber-100 rounded-full">
              <Sparkles className="h-4 w-4 sm:h-5 sm:w-5 text-amber-600" />
            </div>
            <div className="text-left">
              <span className="font-semibold text-amber-900 text-sm sm:text-base block">
                What's new on the platform
              </span>
              <span className="text-[11px] sm:text-xs text-amber-700/80">
                Latest additions · updated Feb 2026
              </span>
            </div>
          </div>
          {open ? (
            <ChevronUp className="h-5 w-5 text-amber-600" />
          ) : (
            <ChevronDown className="h-5 w-5 text-amber-600" />
          )}
        </button>
      </CollapsibleTrigger>

      <CollapsibleContent className="mt-3 space-y-3" data-testid="whats-new-content">
        {items.map((item) => (
          <section
            key={item.id}
            data-testid={`whats-new-item-${item.id}`}
            className={`rounded-lg p-4 sm:p-5 border ${
              highlight === item.id
                ? 'bg-amber-50 border-amber-300 ring-2 ring-amber-200'
                : 'bg-white border-slate-200'
            }`}
          >
            <div className="flex items-start gap-3">
              <span className="shrink-0 inline-flex items-center rounded-full bg-amber-100 text-amber-800 text-[10px] sm:text-[11px] font-semibold px-2 py-0.5 mt-0.5">
                {item.badge}
              </span>
              <div className="min-w-0">
                <h4 className="text-xs sm:text-sm font-semibold text-slate-900 mb-1">
                  {item.title}
                </h4>
                <p className="text-xs sm:text-sm text-gray-700 leading-relaxed">
                  {item.body}
                </p>
              </div>
            </div>
          </section>
        ))}
      </CollapsibleContent>
    </Collapsible>
  );
}
